import { useMemo } from 'react';
import { tokens, Icon, Stack } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';

export interface QuotaCostBannerProps {
  className?: string;
}

/**
 * QuotaCostBanner — GVW-139, GVW-170.
 * 
 * Combined cost budget banner and meter for copilot usage. Shows warnings
 * at ≥80% and errors at 100% budget usage. Includes persistent meter display.
 */
export function QuotaCostBanner({ className }: QuotaCostBannerProps) {
  // Mock cost budget state (replace with actual platform SDK integration)
  const mockCostBudgetState = useViewerStore((state) => state.mockCostBudgetState);

  const costState = useMemo(() => {
    if (!mockCostBudgetState) return null;

    const { used, total, window: timeWindow, resetTime } = mockCostBudgetState;
    const percentage = total > 0 ? (used / total) * 100 : 0;
    
    let severity: 'rest' | 'warning' | 'error' = 'rest';
    let icon = 'monitoring';
    let showBanner = false;

    if (percentage >= 100) {
      severity = 'error';
      icon = 'payments';
      showBanner = true;
    } else if (percentage >= 80) {
      severity = 'warning'; 
      icon = 'notifications_active';
      showBanner = true;
    }

    return {
      used,
      total,
      percentage,
      timeWindow,
      resetTime,
      severity,
      icon,
      showBanner
    };
  }, [mockCostBudgetState]);

  if (!costState) return null;

  const meterComponent = (
    <div
      data-gvw-id="GVW-170"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={costState.total}
      aria-valuenow={costState.used}
      aria-valuetext={`${costState.used} of ${costState.total} expensive runs used in the next ${costState.timeWindow}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        padding: `${tokens.space[1]} ${tokens.space[2]}`,
        background: (() => {
          switch (costState.severity) {
            case 'warning': return 'var(--md-warning-container)';
            case 'error': return 'var(--md-error-container)';
            default: return 'var(--md-surface-container-low)';
          }
        })(),
        color: (() => {
          switch (costState.severity) {
            case 'warning': return 'var(--md-on-warning-container)';
            case 'error': return 'var(--md-on-error-container)';
            default: return tokens.color.ink.primary;
          }
        })(),
        borderRadius: tokens.radius.input,
        fontSize: tokens.font.size.xs,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <Icon name={costState.icon as any} size={16} decorative />
      <div style={{ 
        position: 'relative',
        width: tokens.layout.popoverSm * 0.43 + 'px',
        height: tokens.space[2] + 'px',
        background: 'var(--md-surface-variant)',
        borderRadius: tokens.radius.sm,
        overflow: 'hidden'
      }}>
        {/* 80% notch marker */}
        <div 
          style={{
            position: 'absolute',
            left: '80%',
            top: `${-tokens.space[0] - 2}px`,
            width: `${tokens.space[0] + 2}px`,
            height: `${tokens.space[3]}px`,
            background: 'var(--md-outline)',
            borderRadius: tokens.radius.sm,
            opacity: 0.7,
          }} 
          aria-hidden="true"
        />
        
        {/* Progress fill */}
        <div style={{
          width: `${Math.min(100, costState.percentage)}%`,
          height: '100%',
          background: (() => {
            switch (costState.severity) {
              case 'error': return 'var(--md-error)';
              case 'warning': return 'var(--md-warning)';
              default: return 'var(--md-primary)';
            }
          })(),
          borderRadius: tokens.radius.sm,
          transition: 'width 0.2s ease',
        }} />
      </div>
      <span style={{ minWidth: tokens.layout.popoverSm * 0.21 + 'px', textAlign: 'right' }}>
        {Math.round(costState.percentage)}% used
      </span>
      <button
        type="button"
        onClick={() => {
          console.log('[GVW-170] Open billing surface');
        }}
        aria-label="View billing details"
        style={{
          minHeight: tokens.density.rowDense * 0.75 + 'px',
          width: tokens.density.rowDense * 0.75 + 'px',
          border: 'none',
          borderRadius: tokens.radius.md,
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="receipt_long" size={16} decorative />
      </button>
    </div>
  );

  // GVW-139: Cost budget warning/error banner
  const bannerComponent = costState.showBanner ? (
    <aside
      data-gvw-id="GVW-139"
      role={costState.severity === 'error' ? 'alert' : 'status'}
      aria-live="polite"
      style={{
        width: '100%',
        position: 'relative',
        zIndex: tokens.zIndex.panel,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.space[3],
        padding: `${tokens.space[2]} ${tokens.space[3]}`,
        borderBottom: tokens.border.thin,
        borderLeft: `${tokens.space[1]} solid ${costState.severity === 'error' ? 'var(--md-error)' : 'var(--md-warning)'}`,
        background: costState.severity === 'error' ? 'var(--md-error-container)' : 'var(--md-warning-container)',
        color: costState.severity === 'error' ? 'var(--md-on-error-container)' : 'var(--md-on-warning-container)',
        fontFamily: tokens.font.family.ui,
      }}
    >
      <Stack direction="row" align="center" gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Icon name={costState.icon as any} size={20} decorative />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ 
            fontWeight: tokens.font.weight.medium, 
            fontSize: tokens.font.size.sm,
            marginBottom: tokens.space[1]
          }}>
            {costState.severity === 'error' ? 'Cost budget reached' : 'Approaching cost budget'}
          </div>
          <div style={{ 
            fontSize: tokens.font.size.sm,
            opacity: 0.9
          }}>
            {costState.severity === 'error' 
              ? `Expensive copilot operations are paused until reset at ${costState.resetTime}.`
              : `${costState.used} of ${costState.total} expensive runs used in the next ${costState.timeWindow}.`
            }
          </div>
        </div>
        {/* Inline meter for banner view */}
        <div style={{ flexShrink: 0 }}>
          {meterComponent}
        </div>
      </Stack>
      
      <Stack direction="row" gap={2} style={{ flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => {
            console.log('[GVW-139] Open billing surface');
          }}
          style={{
            minHeight: tokens.density.touchMin,
            border: 'none',
            borderRadius: tokens.radius.md,
            background: 'var(--md-primary)',
            color: 'var(--md-on-primary)',
            paddingInline: tokens.space[3],
            cursor: 'pointer',
            fontSize: tokens.font.size.sm,
            fontWeight: tokens.font.weight.medium,
          }}
        >
          View billing
        </button>
      </Stack>
    </aside>
  ) : null;

  return (
    <div className={className}>
      {bannerComponent}
      {/* GVW-170: Persistent cost meter (always shown when cost budget exists) */}
      {!costState.showBanner ? (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: `${tokens.space[1]} ${tokens.space[3]}`,
          background: tokens.color.surface[1],
          borderBottom: tokens.border.thin,
        }}>
          {meterComponent}
        </div>
      ) : null}
    </div>
  );
}